import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Tabs, Badge, notification } from 'antd';
import { PlusOutlined, LogoutOutlined, SettingOutlined, GlobalOutlined, DeleteOutlined, ExclamationCircleOutlined, ExperimentOutlined, FolderOutlined, UndoOutlined, InboxOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import ProjectDropdown from './ProjectDropdown.jsx';
import ThresholdSettingsModal from './ThresholdSettingsModal.jsx';
import GlobalSettingsModal from './GlobalSettingsModal.jsx';
import ProjectPermissionsModal from './ProjectPermissionsModal.jsx';
import { ConfigurationListView } from './ruling';
import ComplianceUsageWidget from './ComplianceUsageWidget.jsx';
import apiService from '../services/apiService';
import logo from '../assets/APPlyzerLogoHead.svg';

const Sidebar = ({ onNewChat, currentSessionId, onSelectSession, currentProject, onProjectChange, onSessionUpdate, onSidebarRefreshCallback }) => {
  const [sessions, setSessions] = useState([]);
  const [archivedSessions, setArchivedSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [isThresholdModalVisible, setIsThresholdModalVisible] = useState(false);
  const [isGlobalSettingsVisible, setIsGlobalSettingsVisible] = useState(false);
  const [isRulingConfigVisible, setIsRulingConfigVisible] = useState(false);
  const [isPermissionsModalVisible, setIsPermissionsModalVisible] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [sessionToArchive, setSessionToArchive] = useState(null);
  const [sessionToRestore, setSessionToRestore] = useState(null);
  const { logout, getAuthToken, hasComplianceLicense } = useAuth();

  const loadChatSessions = useCallback(async () => {
    try {
      console.log('Sidebar: loadChatSessions called, loading sessions for project:', currentProject?.id);
      setLoading(true);
      const token = getAuthToken();
      const response = await apiService.getChatSessions(token, currentProject?.id);
      console.log('Sidebar: Received sessions:', response.sessions?.length);
      setSessions(response.sessions || []);
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load chat sessions'
      });
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, getAuthToken]);

  const loadArchivedSessions = useCallback(async () => {
    try {
      setArchivedLoading(true);
      const token = getAuthToken();
      const response = await apiService.getArchivedSessions(token, currentProject?.id, 50);
      setArchivedSessions(response.archived_sessions || []);
    } catch (error) {
      console.error('Failed to load archived sessions:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load archived sessions'
      });
    } finally {
      setArchivedLoading(false);
    }
  }, [currentProject?.id, getAuthToken]);

  useEffect(() => {
    loadChatSessions();
    loadArchivedSessions();
  }, [loadChatSessions, loadArchivedSessions]);

  const updateSessionName = useCallback((sessionId, newName) => {
    setSessions(prevSessions => 
      prevSessions.map(session => 
        session.session_id === sessionId 
          ? { ...session, name: newName }
          : session
      )
    );
  }, []);

  // Expose updateSessionName through prop callback
  React.useEffect(() => {
    if (onSessionUpdate) {
      onSessionUpdate(updateSessionName);
    }
  }, [onSessionUpdate, updateSessionName]);

  // Expose loadChatSessions function for external refresh
  React.useEffect(() => {
    console.log('Sidebar: Registering loadChatSessions callback, onSidebarRefreshCallback available:', !!onSidebarRefreshCallback);
    if (onSidebarRefreshCallback) {
      console.log('Sidebar: Calling onSidebarRefreshCallback with loadChatSessions function');
      onSidebarRefreshCallback(loadChatSessions);
    }
  }, [onSidebarRefreshCallback, loadChatSessions]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return 'Today';
    } else if (diffDays === 2) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    }
  };

  const handleSessionClick = (sessionId) => {
    if (onSelectSession) {
      onSelectSession(sessionId);
    }
  };

  const handleThresholdSettings = () => {
    if (currentProject) {
      setIsThresholdModalVisible(true);
    }
  };

  const handleThresholdModalClose = () => {
    setIsThresholdModalVisible(false);
  };

  const handleThresholdUpdate = () => {
    // Optionally refresh sessions or project data
    loadChatSessions();
  };

  const handleGlobalSettings = () => {
    setIsGlobalSettingsVisible(true);
  };

  const handleGlobalSettingsClose = () => {
    setIsGlobalSettingsVisible(false);
  };

  const handleRulingConfig = () => {
    setIsRulingConfigVisible(true);
  };

  const handleRulingConfigClose = () => {
    setIsRulingConfigVisible(false);
  };

  const handleManagePermissions = () => {
    setIsPermissionsModalVisible(true);
  };

  const handlePermissionsModalClose = () => {
    setIsPermissionsModalVisible(false);
  };

  const handleArchiveSession = async (sessionId, sessionName, e) => {
    e.stopPropagation(); // Prevent triggering the session selection
    
    // If shift key is pressed, archive directly without confirmation
    if (e.shiftKey) {
      try {
        const token = getAuthToken();
        await apiService.archiveChatSession(sessionId, token, currentProject?.id);
        
        // Remove the session from active sessions
        setSessions(prevSessions => 
          prevSessions.filter(session => session.session_id !== sessionId)
        );

        // If the archived session was currently active, clear the selection
        if (currentSessionId === sessionId && onSelectSession) {
          onSelectSession(null);
        }

        // Reload archived sessions to show the newly archived one
        loadArchivedSessions();
        
        notification.success({
          message: 'Success',
          description: 'Session archived successfully. You can restore it from the archived tab.'
        });
      } catch (error) {
        console.error('Failed to archive session:', error);
        notification.error({
          message: 'Error',
          description: error.message || 'Failed to archive session'
        });
      }
    } else {
      // Show confirmation modal for normal click
      setSessionToArchive({ id: sessionId, name: sessionName });
    }
  };

  const confirmArchiveSession = async () => {
    if (!sessionToArchive) return;

    try {
      const token = getAuthToken();
      await apiService.archiveChatSession(sessionToArchive.id, token, currentProject?.id);
      
      // Remove the session from active sessions
      setSessions(prevSessions => 
        prevSessions.filter(session => session.session_id !== sessionToArchive.id)
      );

      // If the archived session was currently active, clear the selection
      if (currentSessionId === sessionToArchive.id && onSelectSession) {
        onSelectSession(null);
      }

      // Reload archived sessions to show the newly archived one
      loadArchivedSessions();
      
      notification.success({
        message: 'Success',
        description: 'Session archived successfully. You can restore it from the archived tab.'
      });

      setSessionToArchive(null);
    } catch (error) {
      console.error('Failed to archive session:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to archive session'
      });
    }
  };

  const handleRestoreSession = (sessionId, sessionName, e) => {
    e.stopPropagation();
    setSessionToRestore({ id: sessionId, name: sessionName });
  };

  const confirmRestoreSession = async () => {
    if (!sessionToRestore) return;

    try {
      const token = getAuthToken();
      await apiService.restoreChatSession(sessionToRestore.id, token, currentProject?.id);
      
      // Remove from archived sessions
      setArchivedSessions(prevSessions => 
        prevSessions.filter(session => session.session_id !== sessionToRestore.id)
      );

      // Reload active sessions to show the restored one
      loadChatSessions();
      
      notification.success({
        message: 'Success',
        description: 'Session restored successfully'
      });

      setSessionToRestore(null);
    } catch (error) {
      console.error('Failed to restore session:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to restore session'
      });
    }
  };

  const handleDeleteSession = (sessionId, sessionName, e) => {
    e.stopPropagation(); // Prevent triggering the session selection
    setSessionToDelete({ id: sessionId, name: sessionName });
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;

    try {
      const token = getAuthToken();
      await apiService.deleteChatSession(sessionToDelete.id, token, currentProject?.id);
      
      // Remove from archived sessions
      setArchivedSessions(prevSessions => 
        prevSessions.filter(session => session.session_id !== sessionToDelete.id)
      );

      notification.success({
        message: 'Success',
        description: 'Session permanently deleted'
      });

      setSessionToDelete(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to delete session'
      });
    }
  };

  const cancelAction = () => {
    setSessionToDelete(null);
    setSessionToArchive(null);
    setSessionToRestore(null);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img src={logo} alt="APPlyzer" style={{ height: "48px", marginBottom: "8px" }} />
        <h1>Compliance</h1>
      </div>

      <div className="sidebar-content">
        <div style={{ marginBottom: "24px" }}>
          <h3
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "var(--text-secondary)",
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              marginBottom: "12px",
              paddingLeft: "4px",
              margin: "0 0 12px 0",
            }}
          >
            Current Project
          </h3>
          <div style={{ display: "flex", gap: "8px", alignItems: "stretch", marginBottom: "8px" }}>
            <div style={{ flex: 1 }}>
              <ProjectDropdown 
                currentProject={currentProject} 
                onProjectChange={onProjectChange} 
                style={{ width: "100%" }}
                canCreateProject={hasComplianceLicense}
              />
            </div>
            <Button
              icon={<SettingOutlined />}
              onClick={handleRulingConfig}
              disabled={!currentProject || (currentProject && !currentProject.is_owner && currentProject.permission_level !== 'manage')}
              title={
                !currentProject 
                  ? "Select a project first" 
                  : (currentProject.is_owner || currentProject.permission_level === 'manage')
                    ? "Configure Rulings for this project"
                    : "Manage permission required to configure rulings"
              }
              style={{
                height: "40px",
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--bg-tertiary)",
                borderColor: "var(--border-secondary)",
                color: "var(--text-secondary)",
              }}
            />
          </div>
          {currentProject && currentProject.is_owner && (
            <Button
              icon={<TeamOutlined />}
              onClick={handleManagePermissions}
              block
              size="small"
              title="Manage project access and permissions"
              style={{
                fontSize: "12px",
                height: "32px",
                backgroundColor: "var(--bg-tertiary)",
                borderColor: "var(--border-secondary)",
                color: "var(--text-secondary)",
              }}
            >
              Manage Access
            </Button>
          )}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <Button
            icon={<GlobalOutlined />}
            onClick={handleGlobalSettings}
            block
            disabled={!hasComplianceLicense}
            title={hasComplianceLicense ? "Configure global settings and AI instructions" : "License required to configure global settings"}
            style={{
              height: "36px",
              marginBottom: "8px",
              backgroundColor: "var(--bg-tertiary)",
              borderColor: "var(--border-secondary)",
              color: "var(--text-secondary)",
            }}
          >
            Global Settings
          </Button>
        </div>

        <ComplianceUsageWidget />

        <Button type="primary" icon={<PlusOutlined />} className="new-chat-button" onClick={handleNewChat} block disabled={!currentProject} title={!currentProject ? "Please select a project first" : "Start a new chat in this project"}>
          New Session
        </Button>

        <div className="conversation-history">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="small"
            tabBarStyle={{
              color: '#ffffff',
              marginBottom: '16px'
            }}
            items={[
              {
                key: "active",
                label: "Active",
                children: (
                  <>
                    {loading ? (
                      <div style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>Loading...</div>
                    ) : sessions.length > 0 ? (
                      sessions.map((session) => (
                        <div key={session.session_id} className={`conversation-item ${currentSessionId === session.session_id ? "active" : ""}`} onClick={() => handleSessionClick(session.session_id)}>
                          <div style={{ flex: 1 }}>
                            <div className="conversation-title">{session.name || `${session.session_id.split("-")[0]}...`}</div>
                            <div className="conversation-time">
                              {formatTime(session.last_activity)} • {session.unique_records_count} records
                            </div>
                          </div>
                          <Button icon={<InboxOutlined />} size="small" type="text" onClick={(e) => handleArchiveSession(session.session_id, session.name || `${session.session_id.split("-")[0]}...`, e)} title="Archive conversation" className="archive-session-btn" style={{ color: "#faad14" }} />
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#64748b",
                          fontSize: "14px",
                        }}
                      >
                        No active compliances.
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: "archived",
                label: (
                  <Badge count={archivedSessions.length} size="small" offset={[10, 0]}>
                    Archived
                  </Badge>
                ),
                children: (
                  <>
                    {archivedLoading ? (
                      <div style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>Loading...</div>
                    ) : archivedSessions.length > 0 ? (
                      archivedSessions.map((session) => (
                        <div key={session.session_id} className="conversation-item archived" style={{ opacity: 0.7 }}>
                          <div style={{ flex: 1 }}>
                            <div className="conversation-title">{session.name || `${session.session_id.split("-")[0]}...`}</div>
                            <div className="conversation-time">
                              Archived: {formatTime(session.archived_at)} • {session.message_count} messages
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "4px" }}>
                            <Button icon={<UndoOutlined />} size="small" type="text" onClick={(e) => handleRestoreSession(session.session_id, session.name || `${session.session_id.split("-")[0]}...`, e)} title="Restore conversation" className="restore-session-btn" style={{ color: "#52c41a" }} />
                            <Button icon={<DeleteOutlined />} size="small" type="text" danger onClick={(e) => handleDeleteSession(session.session_id, session.name || `${session.session_id.split("-")[0]}...`, e)} title="Delete permanently" className="delete-session-btn" />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "#64748b",
                          fontSize: "14px",
                        }}
                      >
                        No archived compliances.
                      </div>
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>

        <Button icon={<LogoutOutlined />} className="logout-button" onClick={logout} block>
          Logout
        </Button>
      </div>

      {/* Archive Confirmation Modal */}
      <Modal title="Archive Conversation" open={!!sessionToArchive} onOk={confirmArchiveSession} onCancel={cancelAction} okText="Archive" okType="default" cancelText="Cancel" icon={<InboxOutlined />}>
        <p>Are you sure you want to archive this conversation?</p>
        <p>
          <strong>"{sessionToArchive?.name}"</strong>
        </p>
        <p>You can restore it later from the archived tab.</p>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal title="Restore Conversation" open={!!sessionToRestore} onOk={confirmRestoreSession} onCancel={cancelAction} okText="Restore" okType="primary" cancelText="Cancel" icon={<UndoOutlined />}>
        <p>Are you sure you want to restore this conversation?</p>
        <p>
          <strong>"{sessionToRestore?.name}"</strong>
        </p>
        <p>It will be moved back to your active conversations.</p>
      </Modal>

      {/* Permanent Delete Confirmation Modal */}
      <Modal title="Permanently Delete Conversation" open={!!sessionToDelete} onOk={confirmDeleteSession} onCancel={cancelAction} okText="Delete Forever" okType="danger" cancelText="Cancel" icon={<ExclamationCircleOutlined />}>
        <p>Are you sure you want to permanently delete this conversation?</p>
        <p>
          <strong>"{sessionToDelete?.name}"</strong>
        </p>
        <p style={{ color: "#ff4d4f", fontWeight: "500" }}>This action cannot be undone and all chat history will be lost forever.</p>
      </Modal>

      <ThresholdSettingsModal visible={isThresholdModalVisible} onClose={handleThresholdModalClose} project={currentProject} onThresholdUpdate={handleThresholdUpdate} />
      <GlobalSettingsModal visible={isGlobalSettingsVisible} onClose={handleGlobalSettingsClose} />
      <ProjectPermissionsModal visible={isPermissionsModalVisible} onClose={handlePermissionsModalClose} project={currentProject} />

      <Modal title="Ruling Configuration Management" open={isRulingConfigVisible} onCancel={handleRulingConfigClose} width="95%" style={{ top: 20 }} footer={null}>
        <ConfigurationListView projectId={currentProject?.id} showCreateButton={true} />
      </Modal>
    </div>
  );
};

export default Sidebar;